import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import GlobalPlanningPage from "@/pages/GlobalPlanningPage";
import FlightsPage from "@/pages/FlightsPage";
import CanceledFlightsPage from "@/pages/CanceledFlightsPage";
import StatisticsPage from "@/pages/StatisticsPage";
import ServicesPage from "@/pages/ServicesPage";
import UserManagementPage from "@/pages/UserManagementPage";
import LoginHistoryPage from "@/pages/LoginHistoryPage";
import SettingsPage from "@/pages/SettingsPage";
import AircraftDataPage from "@/pages/AircraftDataPage";
import AirportDataPage from "@/pages/AirportDataPage";
import ImportExportPage from "@/pages/ImportExportPage";
import PermissionsPage from "@/pages/PermissionsPage";
import ApronManagementPage from "@/pages/ApronManagementPage";
import ParkingConfigPage from "@/pages/ParkingConfigPage";
import AIPInfoPage from "@/pages/AIPInfoPage";
import ProceduresPage from "@/pages/ProceduresPage";
import EmailPendingPage from "@/pages/EmailPendingPage";
import EmailSettingsPage from "@/pages/EmailSettingsPage";
import DailyPlanningsPage from "@/pages/DailyPlanningsPage";
import SystemHealthPage from "@/pages/SystemHealthPage";
import FinancialStatisticsPage from "@/pages/FinancialStatisticsPage";
import ModuleSelectPage from "@/pages/ModuleSelectPage";
import CargoLayout from "@/components/CargoLayout";
import CargoDashboardPage from "@/pages/CargoDashboardPage";
import CargoInFlightPage from "@/pages/CargoInFlightPage";
import CargoGlobalPlanningPage from "@/pages/CargoGlobalPlanningPage";
import CargoServicesPage from "@/pages/CargoServicesPage";
import CargoAgencyPage from "@/pages/CargoAgencyPage";
import CargoInvoicingPage from "@/pages/CargoInvoicingPage";
import CargoBackupPage from "@/pages/CargoBackupPage";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PermissionProvider, usePermissions } from "@/context/PermissionContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import InstallPWA from "@/components/InstallPWA";

const ProtectedRoute = ({ children, section = null }) => {
  const { user, loading: authLoading } = useAuth();
  const { hasPermission, loading: permLoading } = usePermissions();
  const location = useLocation();

  // Show loading while checking auth or permissions
  if (authLoading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#d4af37] border-t-transparent"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // Check permissions
  if (section && !hasPermission(section, "view")) {
    return <Navigate to="/dashboard" />;
  }

  return <Layout>{children}</Layout>;
};

const CargoProtectedRoute = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { hasPermission, isMasterAdmin, loading: permLoading } = usePermissions();
  const location = useLocation();

  if (authLoading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} />;
  }
  if (!(isMasterAdmin || hasPermission("cargo", "view"))) {
    return <Navigate to="/select-module" />;
  }
  return <CargoLayout>{children}</CargoLayout>;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/select-module" /> : <LoginPage />} />
      <Route path="/select-module" element={user ? <ModuleSelectPage /> : <Navigate to="/login" />} />
      <Route path="/cargo/dashboard" element={<CargoProtectedRoute><CargoDashboardPage /></CargoProtectedRoute>} />
      <Route path="/cargo/inflight" element={<CargoProtectedRoute><CargoInFlightPage /></CargoProtectedRoute>} />
      <Route path="/cargo/planning" element={<CargoProtectedRoute><CargoGlobalPlanningPage /></CargoProtectedRoute>} />
      <Route path="/cargo/services" element={<CargoProtectedRoute><CargoServicesPage /></CargoProtectedRoute>} />
      <Route path="/cargo/agencies" element={<CargoProtectedRoute><CargoAgencyPage /></CargoProtectedRoute>} />
      <Route path="/cargo/invoicing" element={<CargoProtectedRoute><CargoInvoicingPage /></CargoProtectedRoute>} />
      <Route path="/cargo/backup" element={<CargoProtectedRoute><CargoBackupPage /></CargoProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute section="dashboard"><DashboardPage /></ProtectedRoute>} />
      <Route path="/global-planning" element={<ProtectedRoute section="global_planning"><GlobalPlanningPage /></ProtectedRoute>} />
      <Route path="/daily-plannings" element={<ProtectedRoute><DailyPlanningsPage /></ProtectedRoute>} />
      <Route path="/flights" element={<ProtectedRoute section="flights"><FlightsPage /></ProtectedRoute>} />
      <Route path="/canceled-flights" element={<ProtectedRoute section="canceled_flights"><CanceledFlightsPage /></ProtectedRoute>} />
      <Route path="/statistics" element={<ProtectedRoute section="statistics"><StatisticsPage /></ProtectedRoute>} />
      <Route path="/apron-management" element={<ProtectedRoute section="global_planning"><ApronManagementPage /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute section="services"><ServicesPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute section="users"><UserManagementPage /></ProtectedRoute>} />
      <Route path="/login-history" element={<ProtectedRoute section="login_history"><LoginHistoryPage /></ProtectedRoute>} />
      <Route path="/system-health" element={<ProtectedRoute section="login_history"><SystemHealthPage /></ProtectedRoute>} />
      <Route path="/financial-statistics" element={<ProtectedRoute><FinancialStatisticsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute section="settings"><SettingsPage /></ProtectedRoute>} />
      <Route path="/parking-config" element={<ProtectedRoute section="settings"><ParkingConfigPage /></ProtectedRoute>} />
      <Route path="/aircraft-data" element={<ProtectedRoute section="aircraft_data"><AircraftDataPage /></ProtectedRoute>} />
      <Route path="/airport-data" element={<ProtectedRoute section="airport_data"><AirportDataPage /></ProtectedRoute>} />
      <Route path="/import-export" element={<ProtectedRoute section="import_export"><ImportExportPage /></ProtectedRoute>} />
      <Route path="/permissions" element={<ProtectedRoute section="permissions"><PermissionsPage /></ProtectedRoute>} />
      <Route path="/aip-info" element={<ProtectedRoute><AIPInfoPage /></ProtectedRoute>} />
      <Route path="/procedures" element={<ProtectedRoute><ProceduresPage /></ProtectedRoute>} />
      <Route path="/email-pending" element={<ProtectedRoute><EmailPendingPage /></ProtectedRoute>} />
      <Route path="/email-settings" element={<ProtectedRoute section="settings"><EmailSettingsPage /></ProtectedRoute>} />
      <Route path="/" element={user ? <Navigate to="/select-module" /> : <Navigate to="/login" />} />
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <PermissionProvider>
            <AppRoutes />
            <InstallPWA />
            <Toaster position="top-right" richColors />
          </PermissionProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
